import React, { useRef, useEffect, useState } from 'react';
import SignaturePadLibrary from 'signature_pad';
import { Button } from 'antd';
import { DeleteOutlined, SaveOutlined } from '@ant-design/icons';

/** Ant Design buttons match DSR theme (#ff4d4f). @crc-evans/ui-components used teal/purple primary+tertiary. */

const DSRSignaturePad = ({ visible, onClose, onSave, title, initialSignature }) => {
  const canvasRef = useRef(null);
  const signaturePadRef = useRef(null);
  const [savedSignature, setSavedSignature] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [clearedByUser, setClearedByUser] = useState(false);

  useEffect(() => {
    if (visible) {
      setClearedByUser(false);
      setTimeout(() => {
        initializeSignaturePad();
      }, 100);
    }
    // Cleanup
    return () => {
      if (signaturePadRef.current) {
        signaturePadRef.current.off();
        signaturePadRef.current = null;
      }
    };
    // eslint-disable-next-line
  }, [visible]);

  const initializeSignaturePad = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      canvas.width = canvas.offsetWidth * ratio;
      canvas.height = 150 * ratio;
      canvas.getContext('2d').scale(1, 1);
      signaturePadRef.current = new SignaturePadLibrary(canvas, {
        backgroundColor: 'transparent',
        penColor: 'gray',
        onBegin: () => setIsDrawing(true),
        onEnd: () => setIsDrawing(false),
      });
      // Only load into pad if it's a data URL (e.g. data:image/png;base64,...).
      // After refetch, API may return an S3/http URL; fromDataURL() throws on non-data URLs.
      const isDataUrl = typeof initialSignature === 'string' && initialSignature.startsWith('data:');
      if (isDataUrl) {
        try {
          signaturePadRef.current.fromDataURL(initialSignature);
          setSavedSignature(initialSignature);
        } catch (error) {
          console.error('Failed to load signature:', error);
        }
      }
    }
  };

  const handleSave = () => {
    if (!signaturePadRef.current) {
      return;
    }

    if (signaturePadRef.current.isEmpty()) {
      if (clearedByUser) {
        if (onSave) onSave(null);
      }
      onClose();
      return;
    }

    const base64Image = signaturePadRef.current.toDataURL();
    setSavedSignature(base64Image);
    if (onSave) onSave(base64Image);
    onClose();
  };

  const handleClear = () => {
    if (signaturePadRef.current) {
      signaturePadRef.current.clear();
    }
    setSavedSignature(null);
    setClearedByUser(true);
  };

  return (
    <div>
      <div style={{ border: '1px solid #ddd', borderRadius: 12, padding: 8, marginBottom: 12 }}>
        <canvas
          ref={canvasRef}
          style={{ width: '100%', height: 150, marginBottom: 8 }}
        />
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, flexWrap: 'wrap' }}>
          <Button
            type="default"
            icon={<DeleteOutlined />}
            onClick={handleClear}
            style={{
              borderColor: '#ff4d4f',
              color: '#ff4d4f',
              background: 'transparent',
            }}
          >
            Clear
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSave}
            style={{
              backgroundColor: '#ff4d4f',
              borderColor: '#ff4d4f',
            }}
          >
            Save
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DSRSignaturePad;
